import { Product, ProductCompanyCompany } from "src/api/client-api/products/useGetOneProduct";
import { CompletionMetadata, Completions, CompletionService } from "../../CompletionService";
import { PostList } from "src/api/client-api/post/types/postList.types";

type EntityId = number;
export type ProductCompletionsMetadata = Record<EntityId, CompletionMetadata>;

type ConstructorOptions = {
    product: Product | undefined;
    productPosts: PostList[];
};

export class ProductCompletionService extends CompletionService {
    private productMetadata: ProductCompletionsMetadata = {};
    private readonly companies: ProductCompanyCompany[];
    private readonly productPosts: PostList[];

    public constructor(options: ConstructorOptions) {
        super();
        const { product, productPosts } = options;

        this.productPosts = productPosts;

        const productCompanies = product?.shares?.map(({ organization }) => organization) ?? [];
        const companies = product?.ownedBy ? [product.ownedBy, ...productCompanies] : productCompanies;
        this.companies = companies.filter(
            (company, index) => companies.findIndex(({ id }) => id === company.id) === index,
        );

        this.computeProductMetadata();
    }

    public getMetadata(): ProductCompletionsMetadata {
        return this.productMetadata;
    }

    private computeProductMetadata() {
        const initialAcc: ProductCompletionsMetadata = {};
        const metadata = this.companies.reduce((acc, company) => {
            const companyPosts = this.productPosts.filter((post) => post.organization?.id === company.id);

            const companyMetadata: CompletionMetadata = {
                isValid: true,
                hasProgress: companyPosts.length > 0,
                reference: {
                    score: undefined,
                    label: undefined,
                },
                posts: companyPosts,
            };
            acc[company.id] = companyMetadata;
            return acc;
        }, initialAcc);

        this.productMetadata = metadata;
    }

    public getCompletions(): Completions {
        const emptyCompletion = this.getEmptyCompletions();

        let grandTotalAverage = 0;
        const productCompletions = this.companies.reduce((acc, company) => {
            const metadata = this.productMetadata[company.id];
            const completion = this.getPostListCompletion(metadata.posts);

            acc.byEntityId[company.id] = completion;
            acc.totalAverageItems += completion.totalAverageItems;
            acc.completedCount += completion.completedCount;
            acc.count += completion.count;
            grandTotalAverage += completion.weightedAverage * completion.totalAverageItems;

            return acc;
        }, emptyCompletion);

        if (productCompletions.totalAverageItems > 0) {
            productCompletions.weightedAverage = grandTotalAverage / productCompletions.totalAverageItems;
        }

        return productCompletions;
    }
}
